import { canonicalJson, fnv1a } from "../model/hash"

/** Table ids as engine names: letters, digits and underscore only. */
export const safeId = (id: string): string => id.replace(/[^A-Za-z0-9_]/g, "_")

export const ref = (path: string): string => `<<${path}>>`

/** Text the scripts cannot carry: a double quote, a line break, or the path brackets. */
export const unsafeText = (text: string): boolean => /["\r\n]|<<|>>/.test(text)

/**
 * Text that must become ONE path segment (a heading, a net name, an option
 * label): also refused when it is empty, holds the separator, or ends with
 * `>`, which would merge with the closing brackets. A segment always follows
 * `//`, so a leading `<` or `>` is safe ("<18", ">65").
 */
export const unsafeLabel = (text: string): boolean =>
  !text.trim() || unsafeText(text) || text.includes("//") || text.trim().endsWith(">")

/** A path put between the brackets as it is: a trailing `>` merges with them (engine run g5). */
export const unsafeRef = (path: string): boolean => path.trim().endsWith(">")

/**
 * `n` as a script writes a number: plain digits, never exponent form. The
 * engine reads "1e-7" as a name and not as a number, which gives a wrong
 * table without an error. A fraction JavaScript writes in exponent form
 * (below 0.000001) is written out in full when 20 decimals hold it exactly.
 * null when `n` cannot be written so: not finite, 1e21 or more (in either
 * direction), or a fraction that needs more than 20 decimals.
 */
export const plainNumber = (n: number): string | null => {
  if (!Number.isFinite(n)) return null
  const text = String(n)
  if (!/e/i.test(text)) return text
  if (Math.abs(n) >= 1) return null
  const fixed = n.toFixed(20).replace(/\.?0+$/, "")
  return Number(fixed) === n ? fixed : null
}

/**
 * Names the finished table keeps for itself: the engine's block of totals
 * ("Totals") and the Base rows made from it. A row or column shown under
 * either would be taken for them, or share their id. Shared with the rule
 * for shown labels (`model/shownLabels.ts`).
 */
export const RESERVED_NAMES = ["totals", "base"]

/** A line break or the path separator: a shown label holds neither. Not global, so `test` keeps no state. */
export const LABEL_BREAKS = /[\r\n]|\/\//

/** Shown names are compared trimmed and in any case. */
export const nameKey = (name: string): string => name.trim().toLowerCase()

/**
 * The one rule for the name a row or column is shown under, used by the
 * compiler and by both editors. Returns the sentence that says why `name`
 * cannot be used, or null when it can: it is empty, it is "Totals" or
 * "Base" in any case, another row or column (`others`, the names already
 * taken in the same table or banner group) has it, or the engine cannot
 * take it as a label. `owner` words the sentences; a clash names the other
 * row or column as it is shown.
 *
 * `use` "display": the name is only shown (a shown label) and never reaches
 * the engine, so the engine's character rule does not apply; it still holds
 * no line break and no "//", the finished table's own separator.
 */
export const refusedName = (
  name: string,
  others: string[],
  owner: "row" | "column" = "row",
  use: "engine" | "display" = "engine"
): string | null => {
  const text = name.trim()
  if (!text) return `A ${owner} needs a name.`
  if (RESERVED_NAMES.includes(nameKey(text)))
    return `"${text}" is a name the finished table keeps for itself.`
  // Named as the other is shown, which can differ in case or spaces.
  const other = others.find((o) => nameKey(o) === nameKey(text))
  if (other !== undefined) return `Another ${owner} already has the name "${other.trim()}".`
  if (use === "display")
    return LABEL_BREAKS.test(text) ? `A label cannot hold a line break or "//".` : null
  if (unsafeLabel(text)) return `"${text}" has a character the engine cannot take.`
  return null
}

/** Before data import every path counts as present: the script is then only a shell. */
export const inLoadedData = (
  catalogue: { knownPaths: Set<string> | null },
  path: string
): boolean => !catalogue.knownPaths || catalogue.knownPaths.has(path)

/** A table with one leaf that is true for everyone. Created once per session. */
export const TOTAL_TABLE = "__ts_total"
export const TOTAL_LABEL = "Total"
export const TOTAL_PREP = [
  `QTABLE ${TOTAL_TABLE}`,
  `FIELD ${ref(`${TOTAL_TABLE}//${TOTAL_LABEL}`)} BOOL true`,
].join("\n")

export interface TableNames {
  result: string
  mean: string
  meanTotal: string
  rows: string
  universe: string
  /** A grid summary's Base under base "answered": who answered at least one of its rows. */
  baseUniverse: string
  score: string
  scores: string
  /** The field of the scores or values with every 0 left out (missing), and its two DTABs. */
  excl0: string
  meanExcl0: string
  meanExcl0Total: string
  /** The check's two fields, None and Several (a table from the data shown as single choice or as a scale). */
  check: string
  /** The check's XTAB. */
  checkResult: string
}

export const tableNames = (tableId: string): TableNames => {
  const id = safeId(tableId)
  return {
    result: `ts_${id}`,
    mean: `ts_${id}_m`,
    meanTotal: `ts_${id}_mt`,
    rows: `__ts_r_${id}`,
    universe: `__ts_u_${id}`,
    baseUniverse: `__ts_ub_${id}`,
    score: `__ts_s_${id}`,
    scores: `__ts_sc_${id}`,
    excl0: `__ts_z_${id}`,
    meanExcl0: `ts_${id}_mz`,
    meanExcl0Total: `ts_${id}_mzt`,
    check: `__ts_x_${id}`,
    checkResult: `ts_${id}_x`,
  }
}

/** The engine name of a banner: it changes whenever the banner's content does. */
export const bannerTableName = (definition: unknown): string =>
  `__ts_b_${fnv1a(canonicalJson(definition))}`
